import { useCallback, useEffect, useState } from 'react';

import { type AnalyticsStatus, useAnalytics } from '@treams/treams-analytics';

import { PendingEmployees, findPendingEmployees } from './helpers/findPendingEmployees';
import { CycleAnalytics } from './models/CycleTypes';

export function useCycleAnalytics(
	cycleTemplateID: string | undefined,
	startDate: Date,
	endDate: Date,
	groupIDs?: string[],
	roleID?: string,
): {
	cycleAnalytics: CycleAnalytics | null;
	status: AnalyticsStatus;
	loading: boolean;
	findPendingEmployees: (managerID: string, momentID: string) => PendingEmployees;
} {
	const { requestCycles, status, loading } = useAnalytics();

	const [cycleAnalytics, setCycleAnalytics] = useState<CycleAnalytics | null>(null);

	useEffect(() => {
		if (!cycleTemplateID) return;

		let active = true;

		const request = () => {
			requestCycles(cycleTemplateID, startDate, endDate, groupIDs, roleID)
				.then((data) => {
					if (active) setCycleAnalytics(data);
				})
				.catch(() => {
					// Worker restarted mid-request; retry against the new generation.
					if (active) request();
				});
		};

		request();

		return () => {
			active = false;
		};
	}, [requestCycles, cycleTemplateID, startDate, endDate, groupIDs, roleID]);

	const findPendingEmployeesForManager = useCallback(
		(managerID: string, momentID: string): PendingEmployees => {
			const manager = cycleAnalytics?.manager.find((item) => item.user.id === managerID);

			return manager
				? findPendingEmployees(manager, momentID)
				: { pendingEmployeeIDs: [], pendingEmployeeNames: [] };
		},
		[cycleAnalytics],
	);

	return { cycleAnalytics, status, loading, findPendingEmployees: findPendingEmployeesForManager };
}
